import { useState } from "react";
import { AlertTriangle, RefreshCw, BellRing, CheckCircle2 } from "lucide-react";

// True while a stage is inside its 24h "new/changed" blue-highlight window.
export function isStageNew(stage) {
  return !!stage?.is_new && (!stage.new_until || new Date(stage.new_until).getTime() > Date.now());
}

// Whether the new pipeline drops a stage that had logged work (progress may reset).
export function bomRefreshIncompatible(order) {
  const newNames = new Set((order?.pending_stages || []).map((s) => s.name));
  return (order?.stages || []).some((s) => !newNames.has(s.name) && (Number(s.qty_done) > 0 || Number(s.qty_shipped) > 0));
}

// Admin: yellow "BOM revised — refresh required" banner with the Apply New BOM action.
export function BomRevisionBanner({ order, isAdmin, onApply }) {
  const [confirming, setConfirming] = useState(false);
  if (!order?.bom_outdated) return null;
  const incompatible = bomRefreshIncompatible(order);
  const handleApply = () => {
    if (incompatible && !confirming) { setConfirming(true); return; }
    setConfirming(false);
    onApply(order);
  };
  return (
    <div className="rounded-md border border-yellow-300 bg-yellow-50 px-3 py-2 space-y-2" data-testid={`bom-revised-banner-${order.order_code}`} onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center gap-2">
        <AlertTriangle className="h-4 w-4 text-yellow-600 shrink-0" />
        <p className="text-[11px] text-yellow-800 font-medium flex-1">BOM Revised. Refresh required — this product's production stages changed.</p>
        {isAdmin && !confirming && (
          <button onClick={handleApply} className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded bg-yellow-600 text-white hover:bg-yellow-700 transition-colors" data-testid={`apply-bom-${order.order_code}`}>
            <RefreshCw className="h-3 w-3" /> Apply New BOM
          </button>
        )}
      </div>
      {isAdmin && confirming && (
        <div className="flex items-center gap-2 rounded border border-red-300 bg-red-50 px-2.5 py-1.5" data-testid={`apply-bom-confirm-${order.order_code}`}>
          <AlertTriangle className="h-4 w-4 text-red-600 shrink-0" />
          <p className="text-[11px] text-red-700 font-medium flex-1">Are you sure? The new stages drop a step that already has logged work — this may reset stage progress.</p>
          <button onClick={handleApply} className="text-[11px] font-semibold px-2.5 py-1 rounded bg-red-600 text-white hover:bg-red-700 transition-colors" data-testid={`apply-bom-confirm-yes-${order.order_code}`}>Apply anyway</button>
          <button onClick={() => setConfirming(false)} className="text-[11px] font-semibold px-2.5 py-1 rounded border border-slate-300 text-slate-600 hover:bg-slate-100 transition-colors" data-testid={`apply-bom-confirm-no-${order.order_code}`}>Cancel</button>
        </div>
      )}
    </div>
  );
}

// Operator: prominent blue notice + required Acknowledge Change button (digital record).
export function BomAckBanner({ order, currentUsername, onAck }) {
  if (!order?.ack_required) return null;
  const acked = (order.acknowledged_by || []).some((a) => a.by === currentUsername);
  return (
    <div className={`flex items-center gap-2 rounded-md border px-3 py-2 ${acked ? "border-emerald-300 bg-emerald-50" : "border-blue-300 bg-blue-50"}`} data-testid={`bom-ack-banner-${order.order_code}`} onClick={(e) => e.stopPropagation()}>
      {acked ? <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0" /> : <BellRing className="h-4 w-4 text-blue-600 shrink-0" />}
      <p className={`text-[11px] font-medium flex-1 ${acked ? "text-emerald-800" : "text-blue-800"}`}>
        {acked ? "You acknowledged the updated production stages." : "Your production stages were updated to the latest BOM version. Review the new steps (highlighted in blue) before logging your next quantity."}
      </p>
      {!acked && (
        <button onClick={() => onAck(order)} className="inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded bg-blue-600 text-white hover:bg-blue-700 transition-colors" data-testid={`ack-bom-${order.order_code}`}>
          <CheckCircle2 className="h-3 w-3" /> Acknowledge Change
        </button>
      )}
    </div>
  );
}
